/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import ReactApexChart from "react-apexcharts";
import axios from "axios";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import StarIcon from "@mui/icons-material/Star";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { content, getCurrentWeekDates } from "../utils";

// ─── Shared date formatters ────────────────────────────────────────────────────
const formatFromDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth() >= 9 ? d.getMonth() + 1 : `0${d.getMonth() + 1}`}-${d.getDate() >= 10 ? d.getDate() : `0${d.getDate()}`} 00:00:00.000`;
};
const formatToDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth() >= 9 ? d.getMonth() + 1 : `0${d.getMonth() + 1}`}-${d.getDate() >= 10 ? d.getDate() : `0${d.getDate()}`} 23:59:59.999`;
};
const getTodayDates = () => {
  const today = new Date();
  return { startDate: formatFromDate(today), endDate: formatToDate(today) };
};
const getCurrentMonthDates = () => {
  const today = new Date();
  return { startDate: formatFromDate(new Date(today.getFullYear(), today.getMonth(), 1)), endDate: formatToDate(today) };
};
const getPreviousMonthDates = () => {
  const today = new Date();
  return {
    startDate: formatFromDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
    endDate: formatToDate(new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59)),
  };
};

// ─── YearComparison helpers ────────────────────────────────────────────────────
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const transformYearData = (apiData) => {
  if (!apiData || typeof apiData !== "object") return [];
  const yearData = [];
  for (const year in apiData) {
    const monthlyData = MONTHS.map((m) => apiData[year][m] || 0);
    const total = monthlyData.reduce((s, v) => s + v, 0);
    yearData.push({ year: parseInt(year), monthlyData, total, average: Math.round(total / 12) });
  }
  yearData.sort((a, b) => a.year - b.year);
  for (let i = 1; i < yearData.length; i++) {
    const prev = yearData[i - 1];
    yearData[i].yoyGrowth = prev.total > 0
      ? parseFloat(((yearData[i].total - prev.total) / prev.total * 100).toFixed(1))
      : 0;
  }
  return yearData;
};

const calculateInsights = (yearData) => {
  if (!yearData?.length) return {};
  const sorted = [...yearData].sort((a, b) => b.total - a.total);
  const insights = { ranking: {}, bestYear: sorted[0], worstYear: sorted[sorted.length - 1] };
  sorted.forEach((y, i) => { insights.ranking[y.year] = i + 1; });
  const withGrowth = yearData.filter((y) => y.yoyGrowth !== undefined).sort((a, b) => (b.yoyGrowth || 0) - (a.yoyGrowth || 0));
  if (withGrowth.length) insights.highestGrowth = { year: withGrowth[0].year, percentage: withGrowth[0].yoyGrowth, from: withGrowth[0].year - 1 };
  let maxVal = 0;
  yearData.forEach((y) => y.monthlyData.forEach((v, idx) => { if (v > maxVal) { maxVal = v; insights.bestMonth = { month: MONTHS[idx], year: y.year, value: v }; } }));
  const curYear = new Date().getFullYear();
  const curData = yearData.find((y) => y.year === curYear);
  if (curData?.yoyGrowth !== undefined) insights.currentYearStatus = { year: curYear, status: curData.yoyGrowth >= 0 ? "On Track" : "Off Track", growth: curData.yoyGrowth };
  const allYears = yearData.map((y) => y.year).sort((a, b) => a - b);
  if (allYears.length) insights.yearRange = { from: allYears[0], to: allYears[allYears.length - 1] };
  return insights;
};

const getMedal = (rank) => {
  if (rank === 1) return { label: "1", color: "#B8860B", bg: "#FFF9C4", border: "#FFD700" };
  if (rank === 2) return { label: "2", color: "#606060", bg: "#F5F5F5", border: "#C0C0C0" };
  if (rank === 3) return { label: "3", color: "#7B4F2E", bg: "#FFECB3", border: "#CD7F32" };
  return null;
};

// ─── Sub-components ────────────────────────────────────────────────────────────
const SummaryCard = ({ year, insights, isSelected, onClick }) => {
  const rank = insights.ranking?.[year.year];
  const medal = getMedal(rank);
  const isPos = year.yoyGrowth >= 0;
  const growthColor = year.yoyGrowth === undefined ? "#94a3b8" : isPos ? "#16a34a" : "#dc2626";
  return (
    <Box onClick={() => onClick(year.year)} sx={{
      flex: "1 1 0", minWidth: 140, background: "#fff", borderRadius: "12px",
      border: isSelected ? "2px solid #3567D6" : "1px solid #e2e8f0",
      opacity: isSelected ? 1 : 0.72, cursor: "pointer",
      transition: "all 0.2s", boxShadow: isSelected ? "0 8px 20px rgba(53,103,214,0.13)" : "0 1px 4px rgba(0,0,0,0.04)",
      transform: isSelected ? "translateY(-3px)" : "none", overflow: "hidden", display: "flex", flexDirection: "column",
      "&:hover": { opacity: 1, boxShadow: "0 6px 16px rgba(53,103,214,0.10)" },
    }}>
      <Box sx={{ height: "3px", background: isSelected ? "#3567D6" : "#e2e8f0", transition: "background 0.2s" }} />
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: isSelected ? "#3567D6" : "#475569" }}>{year.year}</Typography>
        {medal && (
          <Box sx={{ background: medal.bg, border: `1px solid ${medal.border}60`, borderRadius: "6px", px: "6px", py: "2px", display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 800, color: medal.color }}>#{medal.label}</Typography>
          </Box>
        )}
      </Stack>
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 800, color: "#1e293b", letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {year.total.toLocaleString("en-IN")}
        </Typography>
        {year.yoyGrowth !== undefined ? (
          <Typography sx={{ fontSize: "11px", fontWeight: 700, color: growthColor }}>
            {isPos ? "↗" : "↘"} {year.yoyGrowth > 0 ? "+" : ""}{year.yoyGrowth}%
          </Typography>
        ) : <Box sx={{ height: "17px" }} />}
      </Box>
      <Box sx={{ mt: "auto", px: 1.5, py: 1, background: isSelected ? "#eff6ff" : "#f8fafc", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>Avg</Typography>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#475569" }}>{year.average.toLocaleString("en-IN")}</Typography>
      </Box>
    </Box>
  );
};

const InsightItem = ({ icon, label, value, subtext, color, bgColor }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Box sx={{ p: "18px 20px", background: "#fff", borderRadius: "12px", border: "1px solid #e8edf6", boxShadow: "0 2px 8px rgba(0,0,0,0.03)", height: "100%" }}>
      <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
        <Box sx={{ width: 36, height: 36, borderRadius: "9px", background: bgColor, color, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</Box>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: "20px", fontWeight: 800, color: "#1e293b", mb: "3px", letterSpacing: "-0.3px" }}>{value}</Typography>
      <Typography sx={{ fontSize: "12px", fontWeight: 600, color }}>{subtext}</Typography>
    </Box>
  </Grid>
);

const InsightsGrid = ({ insights }) => {
  if (!insights.bestYear) return null;
  return (
    <Box mt={3.5}>
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", mb: 2 }}>Executive Summary</Typography>
      <Grid container spacing={2}>
        <InsightItem icon={<EmojiEventsIcon sx={{ fontSize: 19 }} />} label="Highest Reports Year" value={insights.bestYear.year} subtext={`${insights.bestYear.total.toLocaleString("en-IN")} total reports`} color="#16a34a" bgColor="#f0fdf4" />
        {insights.highestGrowth && <InsightItem icon={<TrendingUpIcon sx={{ fontSize: 19 }} />} label="Highest Growth Year" value={insights.highestGrowth.year} subtext={`+${insights.highestGrowth.percentage}% growth rate`} color="#3567D6" bgColor="#eff6ff" />}
        {insights.bestMonth && <InsightItem icon={<StarIcon sx={{ fontSize: 19 }} />} label="Peak Volume Month" value={`${insights.bestMonth.month} ${insights.bestMonth.year}`} subtext={`${insights.bestMonth.value.toLocaleString("en-IN")} in one month${insights.yearRange ? ` · ${insights.yearRange.from}–${insights.yearRange.to}` : ""}`} color="#d97706" bgColor="#fffbeb" />}
        {insights.currentYearStatus && <InsightItem icon={<CalendarTodayIcon sx={{ fontSize: 19 }} />} label="Current Year" value={insights.currentYearStatus.year} subtext={`${insights.currentYearStatus.growth > 0 ? "+" : ""}${insights.currentYearStatus.growth}% vs prev year`} color={insights.currentYearStatus.growth >= 0 ? "#0891b2" : "#dc2626"} bgColor={insights.currentYearStatus.growth >= 0 ? "#ecfeff" : "#fef2f2"} />}
      </Grid>
    </Box>
  );
};

// ─── Skeleton views ────────────────────────────────────────────────────────────
const DayWeekMonthSkeleton = () => (
  <Stack gap={2.5}>
    {/* Controls row skeleton */}
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Skeleton variant="text" width={220} height={22} sx={{ borderRadius: "6px" }} />
      <Stack direction="row" gap={1}>
        <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rectangular" width={88} height={32} sx={{ borderRadius: "8px" }} />
        <Skeleton variant="rectangular" width={90} height={32} sx={{ borderRadius: "8px" }} />
      </Stack>
    </Stack>
    {/* Stat chips skeleton */}
    <Stack direction="row" gap={1.5} flexWrap="wrap">
      <Skeleton variant="rectangular" width={168} height={52} sx={{ borderRadius: "10px" }} />
      <Skeleton variant="rectangular" width={168} height={52} sx={{ borderRadius: "10px" }} />
      <Skeleton variant="rectangular" width={168} height={52} sx={{ borderRadius: "10px" }} />
    </Stack>
    {/* Chart skeleton */}
    <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: "10px" }} />
  </Stack>
);

const YearlySkeleton = () => (
  <Stack gap={3}>
    <Stack direction="row" gap={1.5}>
      {[1, 2, 3, 4, 5].map((k) => <Skeleton key={k} variant="rectangular" sx={{ flex: "1 1 0", minWidth: 140, height: 110, borderRadius: "12px" }} />)}
    </Stack>
    <Skeleton variant="rectangular" width="100%" height={380} sx={{ borderRadius: "10px" }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((k) => <Grid key={k} item xs={12} sm={6} md={3}><Skeleton variant="rectangular" height={110} sx={{ borderRadius: "12px" }} /></Grid>)}
    </Grid>
  </Stack>
);

// ─── Tab config ────────────────────────────────────────────────────────────────
const TABS = [
  { key: "daily",   label: "Daily" },
  { key: "weekly",  label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "yearly",  label: "Yearly" },
];

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const UnifiedReportAnalysis = ({ productType, navBarTriggered }) => {
  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("weekly");

  // ── Day/Week/Month state ───────────────────────────────────────────────────
  const [fetchedData, setFetchedData]   = useState([]);
  const [prevMonthData, setPrevMonthData] = useState("");
  const [loginData, setLoginData]       = useState("");
  const [loader, setLoader]             = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(false);
  const [reRenderComponent, setReRenderComponent] = useState(false);
  const [showAlert, setShowAlert]       = useState(true);
  const [chartType, setChartType]       = useState("bar");
  const [filterValue, setFilterValue]   = useState("all");

  // custom date dialog
  const [show, setShow]                 = useState(false);
  const handleClose = () => setShow(false);
  const handleShow  = () => setShow(true);
  const [customActive, setCustomActive] = useState(false);
  const [selectedFromDate, setSelectedFromDate] = useState("");
  const [selectedToDate, setSelectedToDate]     = useState("");
  const [epochFromTIme, setEpochFromTime]       = useState("");
  const [epochToTIme, setEpochToTime]           = useState("");
  const [maxcalenderDate, SetMaxCalenderDate]   = useState("");

  // ── Yearly state ───────────────────────────────────────────────────────────
  const [yearMonthData, setYearMonthData] = useState(null);
  const [selectedYears, setSelectedYears] = useState([]);
  const [viewMode, setViewMode]           = useState("monthly");
  const [showTrend, setShowTrend]         = useState(false);

  // ── On mount ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const info = JSON.parse(localStorage.getItem("loginDetails"));
    if (info) setLoginData(info);
  }, [reRenderComponent]);

  useEffect(() => {
    const handler = () => setReRenderComponent((p) => !p);
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // ─── API helpers ─────────────────────────────────────────────────────────
  const getFilterLabel = (v) => v === "opened" ? "status" : v === "success" ? "payment_status" : "all";

  const getDayWiseCounts = async (startDate, endDate) => {
    setLoader(true);
    const clientPreference   = JSON.parse(localStorage.getItem("clientPreference"));
    const orgPreference      = JSON.parse(localStorage.getItem("orgPreference"));
    const clientIdPreference = JSON.parse(localStorage.getItem("clientIdPreference"));
    const idToken            = localStorage.getItem("idTokenBilling");
    const url = productType === "vizapp"
      ? `https://api.niroggyan.com/billing/${productType}/counts?query_on=counts&query_by=range&start=${startDate}&end=${endDate}&filter_by=${getFilterLabel(filterValue)}&filter_value=${filterValue}`
      : `https://api.niroggyan.com/billing/${productType}/counts?query_on=counts&query_by=range&start=${startDate}&end=${endDate}${orgPreference && clientPreference ? `&org=${orgPreference}&center=${clientPreference}` : ""}${clientIdPreference ? `&clientId=${clientIdPreference}` : ""}`;
    const res = await axios({ headers: { authorization: idToken }, method: "get", url, validateStatus: (s) => s >= 200 && s < 510 });
    setLoader(false);
    return res;
  };

  const getMonthYearData = async () => {
    const clientPreference   = JSON.parse(localStorage.getItem("clientPreference"));
    const orgPreference      = JSON.parse(localStorage.getItem("orgPreference"));
    const clientIdPreference = JSON.parse(localStorage.getItem("clientIdPreference"));
    const idToken            = localStorage.getItem("idTokenBilling");
    const url = `https://api.niroggyan.com/billing/${productType}/counts?query_on=counts&query_by=monthYear${orgPreference && clientPreference ? `&org=${orgPreference}&center=${clientPreference}` : ""}${clientIdPreference ? `&clientId=${clientIdPreference}` : ""}`;
    return axios({ headers: { authorization: idToken }, method: "get", url, validateStatus: (s) => s >= 200 && s < 510 });
  };

  // ─── Load data by tab ─────────────────────────────────────────────────────
  const loadTabData = async (tab) => {
    setCustomActive(false);
    setShowAlert(true);
    // Clear existing data immediately so skeleton shows on every tab switch
    if (tab !== "yearly") setFetchedData([]);
    if (tab === "daily") {
      const { startDate, endDate } = getTodayDates();
      const res = await getDayWiseCounts(startDate, endDate);
      setFetchedData(res.data.data || []);
    } else if (tab === "weekly") {
      const { startDate, endDate } = getCurrentWeekDates();
      const res = await getDayWiseCounts(startDate, endDate);
      setFetchedData(res.data.data || []);
    } else if (tab === "monthly") {
      const { startDate, endDate } = getCurrentMonthDates();
      const res = await getDayWiseCounts(startDate, endDate);
      setFetchedData(res.data.data || []);
    } else if (tab === "yearly") {
      if (yearMonthData) return; // already loaded
      const stored = JSON.parse(localStorage.getItem("yearWiseData"));
      if (stored && Object.keys(stored).length) { setYearMonthData(stored); return; }
      try {
        const res = await getMonthYearData();
        if (res?.data?.data) { setYearMonthData(res.data.data); localStorage.setItem("yearWiseData", JSON.stringify(res.data.data)); }
      } catch (e) { console.error("Year data error:", e); }
    }
  };

  // ─── Re-load when tab or productType changes ──────────────────────────────
  useEffect(() => {
    localStorage.removeItem("yearWiseData");
    setYearMonthData(null);
    setFetchedData([]);
    setSelectedYears([]);
    loadTabData(activeTab);
  }, [productType]);

  useEffect(() => {
    if (productType === "vizapp" && activeTab !== "yearly") {
      loadTabData(activeTab);
    }
  }, [filterValue]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCustomActive(false);
    setShowAlert(true);
    loadTabData(tab);
  };

  // ─── Custom date apply ────────────────────────────────────────────────────
  const applyCustomDate = async () => {
    handleClose();
    setLoader(true);
    const res = await getDayWiseCounts(epochFromTIme, epochToTIme);
    setFetchedData(res.data.data || []);
    setCustomActive(true);
    setLoader(false);
  };

  const setMaxDateCalender = (date) => {
    const d = new Date(date);
    const max = new Date(d.setDate(d.getDate() + 31));
    const m = max.getMonth() < 9 ? `0${max.getMonth() + 1}` : max.getMonth() + 1;
    const day = max.getDate() < 10 ? `0${max.getDate()}` : max.getDate();
    SetMaxCalenderDate(`${max.getFullYear()}-${m}-${day}`);
  };

  // ─── Download ─────────────────────────────────────────────────────────────
  const downloadMetricsHandler = async () => {
    const idToken = localStorage.getItem("idTokenBilling");
    try {
      setDownloadStatus(true);
      let startDate, endDate;
      if (customActive) { startDate = epochFromTIme; endDate = epochToTIme; }
      else if (activeTab === "daily")   ({ startDate, endDate } = getTodayDates());
      else if (activeTab === "weekly")  ({ startDate, endDate } = getCurrentWeekDates());
      else                              ({ startDate, endDate } = getCurrentMonthDates());
      const endpoint = `https://api.niroggyan.com/billing/${productType}/data?query_on=counts&query_by=range&start=${startDate}&end=${endDate}`;
      const res = await axios({ method: "GET", url: endpoint, headers: { "Content-Type": "application/json", authorization: idToken } });
      const link = document.createElement("a");
      link.href = res.data.data;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) { console.log("download error:", e); }
    finally { setDownloadStatus(false); }
  };

  // ─── Day/Week/Month derived values ────────────────────────────────────────
  const reportCount = fetchedData ? fetchedData.map((e) => parseInt(e.count)) : [];
  const yAxisDates  = fetchedData ? fetchedData.map((e) => e.date.split("-").reverse().join("-").slice(0, -5)) : [];

  const totalCount  = reportCount.reduce((a, b) => a + b, 0);
  const avgDisplay  = totalCount === 0 ? 0 : Math.round(totalCount / (yAxisDates.length || 1));

  let peakCount = 0, peakDate = null;
  if (reportCount.length > 0) {
    const peakIdx = reportCount.reduce((maxI, v, i) => parseInt(v) > parseInt(reportCount[maxI]) ? i : maxI, 0);
    peakCount = parseInt(reportCount[peakIdx]);
    peakDate  = yAxisDates[peakIdx] || null;
  }

  // bar colours – highlight yesterday orange if below 50% avg
  const todayObj    = new Date();
  const yestObj     = new Date(todayObj); yestObj.setDate(todayObj.getDate() - 1);
  const fmtDDMM     = (d) => { const x = new Date(d); const day = x.getDate() < 10 ? `0${x.getDate()}` : x.getDate(); const mo = x.getMonth() + 1 < 10 ? `0${x.getMonth() + 1}` : x.getMonth() + 1; return `${day}-${mo}`; };
  const yestStr     = fmtDDMM(yestObj);
  const threshold   = avgDisplay * 0.5;
  let yestCount = 0, yestFound = false;
  const barColors = yAxisDates.map((ds, idx) => {
    if (ds === yestStr) { yestCount = parseInt(reportCount[idx]) || 0; yestFound = true; return yestCount < threshold ? "#FF9800" : "#3567D6"; }
    return "#3567D6";
  });

  const showVariation = () => {
    if (!prevMonthData?.length) return null;
    const counts = prevMonthData.map((e) => e.count);
    const pAvg   = Math.round(counts.reduce((a, b) => parseInt(a) + parseInt(b)) / prevMonthData.length);
    const pct    = Math.abs(((pAvg - avgDisplay) / pAvg) * 100).toFixed(2);
    const isGt   = avgDisplay > pAvg;
    return (
      <Stack direction="row" gap={0.5} alignItems="center">
        <Typography variant="h6" mt={0.5} color={isGt ? "#0cc437" : "error"}>{pct}%</Typography>
        {isGt ? <ArrowUpwardIcon sx={{ color: "#0cc437" }} fontSize="small" /> : <ArrowDownwardIcon color="error" fontSize="small" />}
      </Stack>
    );
  };

  // chart configs
  const barChartOptions = {
    dataLabels: { enabled: false },
    plotOptions: { bar: { columnWidth: "32%", borderRadius: 4, borderRadiusApplication: "around", distributed: true } },
    legend: { show: false },
    chart: { id: "UnifiedBar", height: 260, redrawOnParentResize: true, redrawOnWindowResize: true, toolbar: { show: true } },
    xaxis: { categories: yAxisDates, labels: { style: { fontSize: "11px" } }, title: { text: "Date", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } } },
    yaxis: { labels: { style: { fontSize: "11px" } }, title: { text: "Reports Generated", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } } },
    colors: barColors,
    grid: { borderColor: "#f0f4ff", strokeDashArray: 4 },
    tooltip: { y: { formatter: (v) => v.toLocaleString("en-IN") } },
  };

  const trendChartOptions = {
    dataLabels: { enabled: false },
    chart: { id: "UnifiedTrend", height: 260, redrawOnParentResize: true, redrawOnWindowResize: true, toolbar: { show: true } },
    stroke: { curve: "smooth", width: 2.5 },
    fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.02, stops: [0, 100] } },
    colors: ["#3567D6"],
    xaxis: { categories: yAxisDates, labels: { style: { fontSize: "11px" } }, title: { text: "Date", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } } },
    yaxis: { labels: { style: { fontSize: "11px" } }, title: { text: "Reports Generated", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } } },
    grid: { borderColor: "#f0f4ff", strokeDashArray: 4 },
    annotations: avgDisplay > 0 ? { yaxis: [{ y: avgDisplay, borderColor: "#f59e0b", borderWidth: 1.5, strokeDashArray: 5, label: { text: `Avg ${avgDisplay.toLocaleString("en-IN")}`, style: { background: "#fff8e1", color: "#92700a", fontSize: "11px", fontWeight: 600, padding: { top: 3, bottom: 3, left: 6, right: 6 } } } }] } : {},
    tooltip: { shared: true, y: { formatter: (v) => v.toLocaleString("en-IN") } },
  };

  // ─── Yearly derived values ─────────────────────────────────────────────────
  const yearData      = useMemo(() => transformYearData(yearMonthData), [yearMonthData]);
  const insights      = useMemo(() => calculateInsights(yearData), [yearData]);
  const availableYrs  = useMemo(() => yearData.map((y) => y.year).sort((a, b) => b - a), [yearData]);
  const allYearTotal  = useMemo(() => yearData.reduce((s, y) => s + y.total, 0), [yearData]);
  const yearWiseCounts = useMemo(() => { const c = {}; yearData.forEach((y) => { c[y.year] = y.total; }); return c; }, [yearData]);

  useEffect(() => {
    if (availableYrs.length > 0 && selectedYears.length === 0) setSelectedYears(availableYrs);
  }, [availableYrs]);

  const filteredYearData = useMemo(() => yearData.filter((y) => selectedYears.includes(y.year)), [yearData, selectedYears]);
  const toggleYear = (yr) => setSelectedYears((prev) => {
    if (prev.includes(yr) && prev.length <= 1) return prev;
    return prev.includes(yr) ? prev.filter((y) => y !== yr) : [...prev, yr].sort((a, b) => a - b);
  });

  const yearChartConfig = useMemo(() => {
    const isYearly   = viewMode === "yearly";
    const baseColors = ["#3567D6","#3CD0FF","#170C6B","#22C55E","#F7B217","#FF6D00","#D50000"];
    const series = isYearly
      ? [{ name: "Total Reports", type: showTrend ? "area" : "bar", data: filteredYearData.map((y) => y.total) }]
      : filteredYearData.map((y) => ({ name: y.year.toString(), type: showTrend ? "line" : "bar", data: y.monthlyData }));
    const categories = isYearly ? filteredYearData.map((y) => y.year.toString()) : MONTHS;
    const colors = isYearly ? ["#3567D6"] : baseColors.slice(0, filteredYearData.length);
    return {
      series,
      options: {
        chart: { type: "line", fontFamily: "inherit", background: "transparent", toolbar: { show: false }, animations: { enabled: true, easing: "easeinout", speed: 800 } },
        colors,
        stroke: { show: true, width: showTrend ? 3 : 1, curve: "smooth", lineCap: "round", colors: showTrend ? undefined : ["#fff"] },
        fill: { type: showTrend && isYearly ? "gradient" : "solid", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.5, inverseColors: false, opacityFrom: 0.6, opacityTo: 0.1, stops: [0, 100] }, opacity: showTrend && isYearly ? 0.6 : 1 },
        plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", columnWidth: isYearly ? "45%" : "85%", dataLabels: { position: "top" }, distributed: false } },
        markers: { size: showTrend ? 5 : 0, hover: { size: 7 }, strokeColors: "#fff", strokeWidth: 2, fillOpacity: 1, shape: "circle" },
        dataLabels: { enabled: isYearly && !showTrend, offsetY: -20, style: { fontSize: "12px", colors: ["#333"] }, formatter: (v) => v >= 1000 ? (v / 1000).toFixed(1) + "k" : v },
        xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { colors: "#64748B", fontSize: "12px", fontWeight: 600 }, offsetY: 2 }, title: { text: isYearly ? "Year" : "Month", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } }, tooltip: { enabled: false } },
        yaxis: { title: { text: "Reports Generated", style: { color: "#94A3B8", fontSize: "11px", fontWeight: 600 } }, labels: { formatter: (v) => v >= 1000000 ? (v / 1000000).toFixed(1) + "M" : v >= 1000 ? (v / 1000).toFixed(1) + "k" : v, style: { colors: "#94A3B8", fontSize: "11px" }, offsetX: -5 } },
        grid: { position: "back", borderColor: "#F1F5F9", strokeDashArray: 4, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } }, padding: { top: 30, right: 0, bottom: 0, left: 20 } },
        tooltip: { theme: "light", style: { fontSize: "12px" }, y: { formatter: (v) => v ? v.toLocaleString() : "0" }, shared: true, intersect: false },
        legend: { show: !isYearly, showForSingleSeries: true, position: "top", horizontalAlign: "left", fontSize: "14px", fontWeight: 600, itemMargin: { horizontal: 10, vertical: 5 } },
      },
    };
  }, [filteredYearData, viewMode, showTrend]);

  // ─── Tab label for chart title ─────────────────────────────────────────────
  const tabChartLabel = { daily: "Daily Report Count", weekly: "Weekly Report Count by Day", monthly: "Monthly Report Count by Day" };

  // Show skeleton whenever loader is active (covers both first load & tab switching)
  const isFirstLoad = loader;

  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{ background: "#fff", borderRadius: "16px", border: "1px solid #e8edf6", boxShadow: "0 2px 16px rgba(53,103,214,0.07)", p: "24px" }}>

      {/* ── Custom Date Dialog ─────────────────────────────────────────── */}
      <Dialog fullWidth maxWidth="xs" open={show} onClose={handleClose}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "15px" }}>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Box>
              <Typography sx={{ fontSize: "13px", color: "#64748b", mb: 0.5 }}>From Date</Typography>
              <input className="customDateTag" type="date" defaultValue={selectedFromDate} onChange={(e) => { setSelectedFromDate(e.target.value); setEpochFromTime(formatFromDate(e.target.value)); setMaxDateCalender(e.target.value); }} style={{ width: "100%" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "13px", color: "#64748b", mb: 0.5 }}>To Date</Typography>
              <input className="customDateTag" type="date" defaultValue={selectedToDate} max={maxcalenderDate} onChange={(e) => { setSelectedToDate(e.target.value); setEpochToTime(formatToDate(e.target.value)); }} style={{ width: "100%" }} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" size="small" onClick={applyCustomDate} sx={{ textTransform: "none", background: "#3567D6", "&:hover": { background: "#2555c0" } }}>Apply</Button>
          <Button variant="outlined" size="small" onClick={handleClose} sx={{ textTransform: "none" }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* ── Card Header ───────────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} mb={2.5}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#1e293b", lineHeight: 1.3 }}>Report Analysis</Typography>
          <Typography sx={{ fontSize: "13px", color: "#94a3b8", mt: "3px" }}>Monitor trends and identify patterns in report generation</Typography>
        </Box>

        {/* All Year Total chip — shown always */}
        {yearData.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ background: "#FFF7ED", border: "1px solid #fde8c8", borderRadius: "10px", px: 2, py: 1, flexShrink: 0, alignSelf: "flex-start" }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: "#F8DEBD", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <AssignmentIcon sx={{ fontSize: 18, color: "#BA8139" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#92700a", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>All Year Total</Typography>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>{allYearTotal.toLocaleString("en-IN")}</Typography>
                {Object.keys(yearWiseCounts).length > 0 && (
                  <Tooltip componentsProps={{ tooltip: { sx: { backgroundColor: "#fff", padding: "0px", border: "1px solid #eceff1", borderRadius: "8px" } } }}
                    title={
                      <Stack gap={0.5}>
                        <Typography sx={{ background: "#eceff1", fontSize: "12px", padding: "6px", color: "#000", fontWeight: 500 }}>Year Wise Counts</Typography>
                        <Box sx={{ padding: "6px" }}>
                          {Object.keys(yearWiseCounts).map((yr) => (
                            <Typography key={yr} sx={{ fontSize: "10px", color: "#000" }}>
                              <span style={{ fontWeight: "normal" }}>{yr} – </span>{yearWiseCounts[yr].toLocaleString("en-IN")}
                            </Typography>
                          ))}
                        </Box>
                      </Stack>
                    } placement="right-start">
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: "#94a3b8", cursor: "pointer" }} />
                  </Tooltip>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>

      {/* ── Tab Switcher ──────────────────────────────────────────────── */}
      <Stack direction="row" sx={{ background: "#F0F4FF", borderRadius: "12px", p: "4px", mb: 3, width: "fit-content" }}>
        {TABS.map(({ key, label }) => (
          <Box key={key} onClick={() => handleTabChange(key)} sx={{
            px: 2.5, py: 0.75, borderRadius: "9px", cursor: "pointer",
            fontSize: "13px", fontWeight: 600, userSelect: "none",
            transition: "all 0.2s ease",
            background: activeTab === key ? "#3567D6" : "transparent",
            color: activeTab === key ? "#fff" : "#64748b",
            boxShadow: activeTab === key ? "0 2px 8px rgba(53,103,214,0.25)" : "none",
          }}>
            {label}
          </Box>
        ))}
      </Stack>

      {/* ══════════════════════════════════════════════════════════════════
          DAILY / WEEKLY / MONTHLY CONTENT
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab !== "yearly" && (
        <>
          {isFirstLoad ? <DayWeekMonthSkeleton /> : (
            <Stack gap={2.5}>
              {/* Controls row */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>
                  {customActive ? "Custom Date Range" : tabChartLabel[activeTab]}
                  {loader && <CircularProgress size={12} sx={{ color: "#3567D6", ml: 1 }} />}
                </Typography>

                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                  {/* VizApp filter */}
                  {productType === "vizapp" && (
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 100 }}>
                      <InputLabel sx={{ fontSize: "12px" }}>Filter</InputLabel>
                      <Select value={filterValue} label="Filter" onChange={(e) => setFilterValue(e.target.value)} sx={{ fontSize: "12px", height: 32 }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="opened">Opened</MenuItem>
                        <MenuItem value="success">Paid</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {/* Custom date icon */}
                  <Tooltip title="Custom date range">
                    <IconButton size="small" onClick={handleShow} sx={{ border: "1px solid #e2e8f0", borderRadius: "8px", width: 32, height: 32, color: customActive ? "#3567D6" : "#64748b", background: customActive ? "#eff6ff" : "transparent" }}>
                      <CalendarMonthIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>

                  {/* Bar | Trend toggle */}
                  <Stack direction="row" sx={{ background: "#F0F4FF", borderRadius: "10px", p: "3px" }}>
                    {[{ key: "bar", icon: <BarChartIcon sx={{ fontSize: 16 }} />, label: "Bar" }, { key: "trend", icon: <ShowChartIcon sx={{ fontSize: 16 }} />, label: "Trend" }].map(({ key, icon, label }) => (
                      <Box key={key} onClick={() => setChartType(key)} sx={{ display: "flex", alignItems: "center", gap: "4px", px: 1.25, py: 0.5, borderRadius: "7px", cursor: "pointer", background: chartType === key ? "#3567D6" : "transparent", transition: "background 0.18s ease" }}>
                        <Box sx={{ color: chartType === key ? "#fff" : "#8897C4", display: "flex", alignItems: "center" }}>{icon}</Box>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: chartType === key ? "#fff" : "#8897C4", lineHeight: 1 }}>{label}</Typography>
                      </Box>
                    ))}
                  </Stack>

                  {/* Download */}
                  <Button size="small" variant="contained" onClick={downloadMetricsHandler}
                    startIcon={downloadStatus ? null : <DownloadIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: "none", height: 32, fontSize: "12px", fontWeight: 600, background: "linear-gradient(135deg,#3567D6,#5a85e8)", borderRadius: "8px", boxShadow: "none", "&:hover": { background: "linear-gradient(135deg,#2555c0,#4a75d8)", boxShadow: "none" } }}>
                    {downloadStatus ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Download"}
                  </Button>
                </Stack>
              </Stack>

              {/* Stat chips */}
              {!loader && reportCount.length > 0 && (
                <Stack direction="row" gap={1.5} flexWrap="wrap">
                  {/* Total Reports */}
                  <Stack direction="row" alignItems="center" gap={1.25} sx={{ background: "#FFF7ED", border: "1px solid #fde8c8", borderRadius: "10px", px: 2, py: 0.875 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "6px", background: "#F8DEBD", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <AssignmentIcon sx={{ fontSize: 16, color: "#BA8139" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#92700a", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>Total Reports</Typography>
                      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{totalCount.toLocaleString("en-IN")}</Typography>
                    </Box>
                  </Stack>

                  {/* Day Average */}
                  <Stack direction="row" alignItems="center" gap={1.25} sx={{ background: "#FFF1F4", border: "1px solid #fdd8e4", borderRadius: "10px", px: 2, py: 0.875 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "6px", background: "#FBF0F3", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#c0395a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#c0395a", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>Day Average</Typography>
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{avgDisplay.toLocaleString("en-IN")}</Typography>
                        {activeTab === "monthly" && showVariation()}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Peak Day */}
                  {peakCount > 0 && peakDate && (
                    <Stack direction="row" alignItems="center" gap={1.25} sx={{ background: "#F0FDF4", border: "1px solid #bbf7d0", borderRadius: "10px", px: 2, py: 0.875 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: "6px", background: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6L12 2z" fill="#16a34a" /></svg>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#15803d", textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>Peak Day</Typography>
                        <Stack direction="row" alignItems="baseline" gap={0.6}>
                          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{peakCount.toLocaleString("en-IN")}</Typography>
                          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#16a34a", lineHeight: 1.3 }}>{peakDate}</Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  )}

                  {/* Reloading */}
                  {loader && fetchedData.length > 0 && <Stack justifyContent="center" pl={0.5}><CircularProgress size={16} sx={{ color: "#3567D6" }} /></Stack>}
                </Stack>
              )}

              {/* Low-usage alert */}
              {!loader && yestCount < threshold && yestFound && (
                <Collapse in={showAlert}>
                  <Alert severity={yestCount === 0 ? "error" : "warning"} variant="standard" onClose={() => setShowAlert(false)}
                    sx={{ borderRadius: "10px", border: yestCount === 0 ? "1px solid #d32f2f" : "1px solid #ff9800", "& .MuiAlert-icon": { fontSize: "22px", mt: "2px" } }}>
                    <AlertTitle sx={{ fontWeight: 700, fontSize: "13px" }}>{yestCount === 0 ? "No Activity Detected" : "Low Usage Detected"}</AlertTitle>
                    <Typography sx={{ fontSize: "12px" }}>
                      {yestCount === 0 ? "No reports were generated yesterday." : `Yesterday's count (${yestCount.toLocaleString("en-IN")}) was significantly lower than the daily average (${avgDisplay.toLocaleString("en-IN")}).`}
                    </Typography>
                  </Alert>
                </Collapse>
              )}

              {/* No data */}
              {!loader && reportCount.length === 0 && (
                <Stack alignItems="center" justifyContent="center" gap={2} py={4}>
                  <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BarChartIcon sx={{ fontSize: 28, color: "#94a3b8" }} />
                  </Box>
                  <Typography sx={{ fontSize: "14px", color: "#94a3b8", fontWeight: 500 }}>No data available for this period</Typography>
                </Stack>
              )}

              {/* Chart */}
              {!loader && reportCount.length > 0 && (
                <Box sx={{ position: "relative" }}>
                  {loader && fetchedData.length > 0 && (
                    <Box sx={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.55)", zIndex: 2, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CircularProgress size={28} sx={{ color: "#3567D6" }} />
                    </Box>
                  )}
                  <Chart key={chartType} options={chartType === "bar" ? barChartOptions : trendChartOptions} series={[{ name: "Reports Generated", data: reportCount }]} type={chartType === "bar" ? "bar" : "area"} height="260px" />
                </Box>
              )}
            </Stack>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          YEARLY CONTENT
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "yearly" && (
        <>
          {yearMonthData === null ? <YearlySkeleton /> : (
            <Stack gap={3}>
              {/* Year summary cards */}
              <Stack direction="row" gap={1.5} sx={{ overflowX: "auto", pb: 0.5 }}>
                {yearData.map((y) => (
                  <SummaryCard key={y.year} year={y} insights={insights} isSelected={selectedYears.includes(y.year)} onClick={toggleYear} />
                ))}
              </Stack>

              {/* Chart panel */}
              <Box sx={{ border: "1px solid #e2e8f0", borderRadius: "14px", p: "20px 24px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} mb={3}>
                  <Box>
                    <Typography sx={{ fontSize: "17px", fontWeight: 700, color: "#1A237E" }}>
                      {viewMode === "monthly" ? "Monthly Trend Analysis" : "Total Yearly Volume"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#94a3b8", mt: "3px" }}>
                      {selectedYears.length} active years · {viewMode === "monthly" ? "Comparing seasonality" : "Comparing total growth"}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Button variant={showTrend ? "contained" : "outlined"} size="small" startIcon={<ShowChartIcon sx={{ fontSize: 15 }} />} onClick={() => setShowTrend(!showTrend)}
                      sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, borderRadius: "10px", height: 32, px: 1.5, ...(showTrend ? { background: "#3567D6", color: "#fff", "&:hover": { background: "#0044ff", color: "#fff" } } : { borderColor: "#c7d2e0", color: "#ffffffff", "&:hover": { borderColor: "#3567D6", background: "#1768d3ff", color: "#fff" } }) }}>
                      {showTrend ? "Hide Trend" : "Show Trend"}
                    </Button>
                    <Stack direction="row" sx={{ background: "#F0F4FF", borderRadius: "10px", p: "3px", gap: "2px" }}>
                      {["monthly", "yearly"].map((mode) => (
                        <Box key={mode} onClick={() => setViewMode(mode)} sx={{ px: 1.5, py: 0.5, borderRadius: "8px", fontSize: "12px", fontWeight: 600, cursor: "pointer", transition: "all 0.2s", background: viewMode === mode ? "#3567D6" : "transparent", color: viewMode === mode ? "#fff" : "#64748b", userSelect: "none", textTransform: "capitalize" }}>
                          {mode.charAt(0).toUpperCase() + mode.slice(1)}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>

                {filteredYearData.length > 0 ? (
                  <ReactApexChart key={viewMode + String(showTrend) + selectedYears.join(",")} options={yearChartConfig.options} series={yearChartConfig.series} type="line" height={400} />
                ) : (
                  <Stack alignItems="center" py={6}>
                    <Typography sx={{ color: "#94a3b8", fontSize: "14px" }}>Select a year to view the chart</Typography>
                  </Stack>
                )}
              </Box>

              {/* Executive Summary */}
              <InsightsGrid insights={insights} />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default UnifiedReportAnalysis;
